import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { productionService } from "../services/production";
import { productParametersService } from "../services/productParameters";
import toast from "react-hot-toast"; // Importar toast
import { formatDateForDisplay, formatDateForInput, formatDateForPortugal } from "../utils/dateUtils";
import { useIsAdmin } from "../hooks/useIsAdmin";
import ParameterFormModal from "../components/ParameterFormModal";
import ParameterStatusButton from "../components/ParameterStatusButton";

const ProductionDetails = () => {
  const [productionOrder, setProductionOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [itemToReject, setItemToReject] = useState(null);
  const [editingCompletionDate, setEditingCompletionDate] = useState(false);
  const [completionDate, setCompletionDate] = useState("");
  const [editingEstimatedDate, setEditingEstimatedDate] = useState(false);
  const [estimatedDate, setEstimatedDate] = useState("");
  const [editingOrderName, setEditingOrderName] = useState(false);
  const [orderName, setOrderName] = useState("");
  const [editingProductionDate, setEditingProductionDate] = useState(false);
  const [productionDate, setProductionDate] = useState("");
  const [calculatedLiters, setCalculatedLiters] = useState({}); // Estado para armazenar litros calculados dinamicamente
  const [savingItems, setSavingItems] = useState({}); // Estado para controlar salvamento de items individuais
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Modal de confirma\u00e7\u00e3o de dele\u00e7\u00e3o
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [itemToSplit, setItemToSplit] = useState(null);
  const [splitKilos, setSplitKilos] = useState("");
  const [parameterModalOpen, setParameterModalOpen] = useState(false); // Modal de parâmetros
  const [selectedItemForParameters, setSelectedItemForParameters] = useState(null); // Item selecionado para parâmetros
  const [itemParametersStatus, setItemParametersStatus] = useState({}); // Status de parâmetros por item { itemId: { hasActiveParameters, parametersFilled } }
  const [existingResponses, setExistingResponses] = useState(null); // Respostas existentes para exibir no modal
  const [selectedLotForParameters, setSelectedLotForParameters] = useState(null); // Lote físico do item (quando já produzido) — parâmetros ficam vinculados a ele
  const [actualProductionDates, setActualProductionDates] = useState({}); // Data real de produção por item (apenas produtos com cura)
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  // Função helper para gerar número de lote baseado no código do produto e data de produção
  const generateLotNumber = (productCode, suffix = '') => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const dateStr = `${day}${month}${year}`; // DDMMYYYY
    return `${productCode}-${dateStr}${suffix}`;
  };

  // Função para calcular litros necessários baseado na produtividade
  const calculateLiters = (kilos, produtividade) => {
    if (!produtividade || produtividade === 0) return 0;
    // Fórmula: litros = (kg / produtividade) * 100
    // Exemplo: 20kg com 20% produtividade = (20 / 20) * 100 = 100L
    return (kilos / produtividade) * 100;
  };

  // Função para formatar a exibição kg/L com cálculo dinâmico
  const formatKgLiters = (item) => {
    const kilos = item.quantity_to_produce || 0;
    // Usar o valor calculado dinamicamente do estado, com fallback para recalcular
    const liters = calculatedLiters[item.id] !== undefined
      ? calculatedLiters[item.id]
      : calculateLiters(kilos, item.products?.produtividade || 20) || 0;
    return `${(kilos || 0).toFixed(2)}kg / ${(liters || 0).toFixed(0)}L`;
  };

  // Função helper para encontrar lote correspondente ao item
  const findLotForItem = (item) => {
    if (!productionOrder?.production_lots) return null;
    return productionOrder.production_lots.find(lot => lot.product_id === item.product_id);
  };

  // Estado para armazenar números de lote editados localmente
  const [localLotNumbers, setLocalLotNumbers] = useState({});

  // Timers de debounce para auto-save
  const saveTimersRef = React.useRef({});

  // Função de auto-save com debounce
  const autoSaveItem = async (itemId, field, value) => {
    const key = `${itemId}-${field}`;

    // Limpar timer anterior se existir
    if (saveTimersRef.current[key]) {
      clearTimeout(saveTimersRef.current[key]);
    }

    // Criar novo timer
    saveTimersRef.current[key] = setTimeout(async () => {
      try {
        setSavingItems(prev => ({ ...prev, [key]: true }));

        const item = productionOrder.production_order_items.find(i => i.id === itemId);
        if (!item) return;

        // Preparar o item atualizado
        const updatedItem = {
          product_id: item.product_id,
          suggested_quantity: item.suggested_quantity,
          quantity_to_produce: item.quantity_to_produce,
          actual_produced_quantity: field === 'actual_produced_quantity' ? value : item.actual_produced_quantity,
          quantity_produced_kg: field === 'quantity_produced_kg' ? value : item.quantity_produced_kg,
          item_status: item.item_status,
          quality_grade: item.quality_grade,
          custom_lot_number: field === 'custom_lot_number' ? value : (localLotNumbers[item.id] || item.custom_lot_number || null),
        };

        // Enviar atualização
        await productionService.updateProductionOrderItems(id, [updatedItem]);

        // Se salvou custom_lot_number, atualizar o estado local
        if (field === 'custom_lot_number') {
          setProductionOrder(prev => ({
            ...prev,
            production_order_items: prev.production_order_items.map(i =>
              i.id === itemId ? { ...i, custom_lot_number: value } : i
            )
          }));

          // Remover do estado local (agora está salvo no banco)
          setLocalLotNumbers(prev => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });
        }

        toast.success("Alteração salva automaticamente", { duration: 1500, icon: "💾" });

      } catch (error) {
        console.error('Erro ao salvar alteração:', error);
        toast.error("Erro ao salvar: " + error.message);
      } finally {
        setSavingItems(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
      }
    }, 1000); // Debounce de 1 segundo
  };

  // Função para atualizar número de lote localmente e salvar automaticamente
  const handleLotNumberInputChange = (itemId, newValue) => {
    console.log('handleLotNumberInputChange chamada:', { itemId, newValue });

    // Atualizar estado local
    setLocalLotNumbers(prev => {
      const updated = {
        ...prev,
        [itemId]: newValue
      };
      console.log('Estado local atualizado:', updated);
      return updated;
    });

    // Verificar se há lote físico para este item
    const item = productionOrder?.production_order_items?.find(i => i.id === itemId);
    if (!item) return;

    const lot = findLotForItem(item);

    if (lot && lot.id && newValue && newValue.trim()) {
      // Se existe lote físico, salvar no lote (com debounce)
      const key = `lot-${lot.id}`;
      if (saveTimersRef.current[key]) {
        clearTimeout(saveTimersRef.current[key]);
      }

      saveTimersRef.current[key] = setTimeout(async () => {
        try {
          await productionService.updateProductionLotNumber(lot.id, newValue);

          setProductionOrder(prev => ({
            ...prev,
            production_lots: prev.production_lots?.map(l =>
              l.id === lot.id ? { ...l, lot_number: newValue } : l
            )
          }));

          // Remover do estado local após salvar
          setLocalLotNumbers(prev => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });

          toast.success("Número do lote salvo automaticamente", { duration: 1500, icon: "💾" });
        } catch (error) {
          console.error('Erro ao salvar número do lote:', error);
          toast.error("Erro ao salvar número do lote: " + error.message);
        }
      }, 1000);
    } else {
      // Se não existe lote físico, salvar no custom_lot_number do item
      autoSaveItem(itemId, 'custom_lot_number', newValue);
    }
  };

  // Função para atualizar a data real de produção localmente e, se já existir
  // lote físico (ordem concluída), salvar automaticamente nele (com debounce)
  const handleActualProductionDateChange = (item, newValue) => {
    setActualProductionDates(prev => ({ ...prev, [item.id]: newValue }));

    const lot = findLotForItem(item);
    if (lot && lot.id) {
      const key = `lot-date-${lot.id}`;
      if (saveTimersRef.current[key]) {
        clearTimeout(saveTimersRef.current[key]);
      }

      saveTimersRef.current[key] = setTimeout(async () => {
        try {
          await productionService.updateProductionLotActualDate(lot.id, newValue);

          setProductionOrder(prev => ({
            ...prev,
            production_lots: prev.production_lots?.map(l =>
              l.id === lot.id ? { ...l, actual_production_date: newValue } : l
            )
          }));

          toast.success("Data real de produção salva automaticamente", { duration: 1500, icon: "💾" });
        } catch (error) {
          console.error('Erro ao salvar data real de produção:', error);
          toast.error("Erro ao salvar data real de produção: " + error.message);
        }
      }, 1000);
    }
  };

  // Função para obter o valor a ser exibido no campo de lote
  const getLotNumberValue = (item) => {
    const lot = findLotForItem(item);
    const suggestedLotNumber = item.products?.product_code
      ? generateLotNumber(item.products.product_code)
      : '';

    // DEBUG: Log para entender o fluxo
    console.log('getLotNumberValue:', {
      itemId: item.id,
      productId: item.product_id,
      lotFound: lot ? { id: lot.id, lot_number: lot.lot_number } : null,
      customLotNumber: item.custom_lot_number,
      localLotNumber: localLotNumbers[item.id],
      suggestedLotNumber,
      totalLots: productionOrder?.production_lots?.length || 0
    });

    // Prioridade: 1) Valor editado localmente, 2) Valor do lote existente, 3) custom_lot_number salvo no item, 4) Valor sugerido
    if (localLotNumbers[item.id] !== undefined) {
      return localLotNumbers[item.id];
    }

    if (lot && lot.lot_number) {
      return lot.lot_number;
    }

    // Se já tem um custom_lot_number salvo no banco, usar ele (não regerar)
    if (item.custom_lot_number) {
      return item.custom_lot_number;
    }

    return suggestedLotNumber;
  };


  // Função para confirmar a rejeição do item
  const handleConfirmReject = async () => {
    if (itemToReject) {
      const { itemId, field, value, currentItem } = itemToReject;
      
      console.log('Frontend: Dados para rejeição:', {
        orderId: id,
        productId: currentItem.product_id,
        currentItem: currentItem,
        actualProducedQuantity: currentItem.actual_produced_quantity || 0
      });
      
      try {
        // Chamar a API para processar a rejeição com baixa de estoque
        const rejectResult = await productionService.rejectProductionItem(
          id,
          currentItem.product_id, // Usar product_id em vez de itemId
          currentItem.actual_produced_quantity || 0,
          currentItem.quantity_produced_kg || 0
        );

        console.log('Frontend: ✅ REJEIÇÃO SUCESSO! Resultado:', rejectResult);
        console.log('%c✅ REJEIÇÃO BEM-SUCEDIDA', 'color: green; font-size: 14px; font-weight: bold');

        // Atualizar o estado local
        setProductionOrder((prevOrder) => ({
          ...prevOrder,
          production_order_items: prevOrder.production_order_items.map((item) =>
            item.id === itemId ? { ...item, [field]: value } : item
          ),
        }));

        toast.success("Item rejeitado com sucesso. A quantidade foi removida do estoque.");

        // ⭐ FORÇAR REFRESH DE STOCK - Sinalizar para a aba de Stock refazer fetch
        console.log('Frontend: 📢 Sinalizando página Stock para refazer fetch...');
        console.log('%c📢 SINALIZANDO REFRESH EM STOCK', 'color: blue; font-size: 14px; font-weight: bold');

        // Sinalizar via localStorage para a página Stock refazer o fetch
        localStorage.setItem('forceStockRefresh', new Date().getTime().toString());
        console.log('Frontend: ✅ Sinal enviado para Stock');

      } catch (error) {
        toast.error("Erro ao processar rejeição: " + error.message);
      }
    }
    setShowRejectModal(false);
    setItemToReject(null);
  };

  // Função para cancelar a rejeição
  const handleCancelReject = () => {
    setShowRejectModal(false);
    setItemToReject(null);
  };

  // Função para deletar ordem de produção (ADMIN ONLY)
  const handleDeleteProduction = async () => {
    try {
      await productionService.deleteProductionOrder(id);
      toast.success("Ordem de produção deletada com sucesso!");
      navigate(-1);
    } catch (error) {
      toast.error(error.message || "Erro ao deletar ordem de produção");
      console.error("Erro ao deletar ordem de produção:", error);
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  // Efeito para recalcular litros dinamicamente quando os dados mudam
  useEffect(() => {
    if (productionOrder && productionOrder.production_order_items) {
      const newCalculatedLiters = {};
      productionOrder.production_order_items.forEach((item) => {
        const kilos = item.quantity_to_produce || 0;
        const produtividade = item.products?.produtividade || 20;
        const liters = calculateLiters(kilos, produtividade);
        newCalculatedLiters[item.id] = liters;
      });
      setCalculatedLiters(newCalculatedLiters);
    }
  }, [productionOrder]);

  useEffect(() => {
    const fetchProductionOrderDetails = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const data = await productionService.getProductionOrderById(id);
        setProductionOrder(data);

        // Inicializar data de conclusão
        setCompletionDate(data.expected_completion_date || "");

        // Inicializar data de previsão
        setEstimatedDate(data.estimated_completion_date || "");

        // Inicializar nome da ordem
        setOrderName(data.order_name || "");

        // Inicializar data de produção
        setProductionDate(data.production_date || "");

        // Inicializar data real de produção por item: usa a data já salva no
        // lote (se já existir, ex. após concluir a ordem), senão a data de hoje
        const today = new Date().toISOString().split('T')[0];
        const initialActualDates = {};
        (data.production_order_items || []).forEach((item) => {
          const lot = (data.production_lots || []).find((l) => l.product_id === item.product_id);
          initialActualDates[item.id] = (lot && lot.actual_production_date)
            ? lot.actual_production_date.split('T')[0]
            : today;
        });
        setActualProductionDates(initialActualDates);

        setError("");

        // Buscar status de parâmetros para cada item
        if (data.production_order_items) {
          fetchParametersStatus(data.production_order_items);
        }
      } catch (err) {
        // Em caso de erro ao buscar, marcar como não encontrado
        console.error("Erro ao buscar detalhes da ordem de produção:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProductionOrderDetails();
  }, [id]);

  // Buscar status de parâmetros para todos os itens
  const fetchParametersStatus = async (items) => {
    const statusMap = {};
    for (const item of items) {
      try {
        const hasParams = await productParametersService.checkProductHasParameters(item.product_id);
        statusMap[item.id] = {
          hasActiveParameters: hasParams,
          parametersFilled: item.parameters_filled || false,
        };
      } catch (error) {
        console.error("Erro ao verificar parâmetros do produto:", item.product_id, error);
        statusMap[item.id] = {
          hasActiveParameters: false,
          parametersFilled: false,
        };
      }
    }
    setItemParametersStatus(statusMap);
  };

  // Abrir modal de parâmetros
  const handleOpenParameterModal = async (item) => {
    setSelectedItemForParameters(item);

    // Se já existe um lote físico para este item (produção concluída), os
    // parâmetros ficam vinculados ao LOTE (production_lot_id), não ao item —
    // a linha em production_order_items pode já ter sido substituída
    // (update_production_order apaga e recria o item ao concluir a ordem).
    // Buscamos e editamos por ali; salvar aqui aplica a alteração
    // diretamente no lote já produzido.
    const lot = (productionOrder?.production_lots || []).find(
      (l) => l.product_id === item.product_id
    );
    setSelectedLotForParameters(lot || null);

    if (lot) {
      try {
        const responses = await productParametersService.getResponsesByLot(lot.id);
        setExistingResponses(responses);
      } catch (error) {
        console.error("Erro ao carregar respostas existentes do lote:", error);
        setExistingResponses(null);
      }
    } else if (item.parameters_filled) {
      try {
        const responses = await productParametersService.getResponses(item.id);
        setExistingResponses(responses);
      } catch (error) {
        console.error("Erro ao carregar respostas existentes:", error);
        setExistingResponses(null);
      }
    } else {
      setExistingResponses(null);
    }

    setParameterModalOpen(true);
  };

  // Fechar modal de parâmetros
  const handleCloseParameterModal = () => {
    setParameterModalOpen(false);
    setSelectedItemForParameters(null);
    setExistingResponses(null);
    setSelectedLotForParameters(null);
  };

  // Callback quando parâmetros são salvos
  const handleParametersSaved = () => {
    // Atualizar status do item
    if (selectedItemForParameters) {
      setItemParametersStatus((prev) => ({
        ...prev,
        [selectedItemForParameters.id]: {
          ...prev[selectedItemForParameters.id],
          parametersFilled: true,
        },
      }));

      // Atualizar o item na lista
      setProductionOrder((prev) => ({
        ...prev,
        production_order_items: prev.production_order_items.map((item) =>
          item.id === selectedItemForParameters.id
            ? { ...item, parameters_filled: true }
            : item
        ),
      }));
    }
  };

  // Verificar se item pode mudar de status
  // Parâmetros não são mais obrigatórios para fechar produção
  const canChangeItemStatus = (item) => {
    return true;
  };

  const handleOpenSplitModal = (item) => {
    setItemToSplit(item);
    setSplitKilos("");
    setShowSplitModal(true);
  };

  const handleConfirmSplit = async () => {
    if (!itemToSplit || !splitKilos) return;
    const kg = parseFloat(splitKilos);
    if (isNaN(kg) || kg <= 0) {
      toast.error("Insira uma quantidade válida em kg.");
      return;
    }
    if (kg > itemToSplit.quantity_to_produce) {
      toast.error(`A quantidade a dividir não pode exceder ${itemToSplit.quantity_to_produce}kg.`);
      return;
    }
    try {
      const result = await productionService.splitProductionItem(id, itemToSplit.id, kg);

      // Atualizar item atual no estado local (remover se ficou com 0kg)
      setProductionOrder((prev) => ({
        ...prev,
        total_quantity_to_produce: Math.max(0, (prev.total_quantity_to_produce || 0) - kg),
        production_order_items: result.remainingKg === 0
          ? prev.production_order_items.filter((i) => i.id !== itemToSplit.id)
          : prev.production_order_items.map((i) =>
              i.id === itemToSplit.id
                ? { ...i, quantity_to_produce: result.remainingKg, suggested_quantity: result.remainingLiters }
                : i
            ),
      }));

      setShowSplitModal(false);
      setItemToSplit(null);
      setSplitKilos("");

      const newOrderId = result.newOrder?.id || result.newOrder?.[0]?.id;
      toast.success(
        (t) => (
          <span>
            Divisão criada com sucesso!{" "}
            {newOrderId && (
              <button
                className="underline font-medium ml-1"
                onClick={() => {
                  toast.dismiss(t.id);
                  window.open(`/production-details/${newOrderId}`, "_blank");
                }}
              >
                Abrir nova ordem
              </button>
            )}
          </span>
        ),
        { duration: 6000 }
      );
    } catch (err) {
      toast.error(err.message || "Erro ao dividir item.");
    }
  };

  const handleItemChange = async (itemId, field, value) => {
    // Se estiver tentando rejeitar um item que estava produzido, mostrar modal de confirmação
    if (field === "item_status" && value === "rejected") {
      const currentItem = productionOrder.production_order_items.find(item => item.id === itemId);
      if (currentItem && currentItem.item_status === "produced") {
        setItemToReject({ itemId, field, value, currentItem });
        setShowRejectModal(true);
        return;
      }
    }

    setProductionOrder((prevOrder) => {
      const updatedOrder = {
        ...prevOrder,
        production_order_items: prevOrder.production_order_items.map((item) =>
          item.id === itemId ? { ...item, [field]: value } : item
        ),
      };

      // Se o campo alterado for quantity_to_produce, recalcular automaticamente os litros
      if (field === "quantity_to_produce") {
        const updatedItem = updatedOrder.production_order_items.find(item => item.id === itemId);
        if (updatedItem) {
          const kilos = updatedItem.quantity_to_produce || 0;
          const produtividade = updatedItem.products?.produtividade || 20;
          const liters = calculateLiters(kilos, produtividade);
          setCalculatedLiters(prev => ({
            ...prev,
            [itemId]: liters
          }));
        }
      }

      return updatedOrder;
    });

    // Salvar automaticamente com debounce para campos de quantidade
    if (field === "actual_produced_quantity" || field === "quantity_produced_kg") {
      autoSaveItem(itemId, field, value);
    }

    // Se o campo alterado for quality_grade, apenas atualizar o estado local
    // A atualização da qualidade será feita quando o status da ordem for atualizado
    if (field === "quality_grade") {
      console.log(`Qualidade do item ${itemId} atualizada para: ${value}`);
    }
  };


  const handleUpdateEstado = async (newEstado) => {
    try {
      // Validar se itens marcados como "produced" têm KG preenchido
      const itemsProduced = productionOrder.production_order_items.filter(
        (item) => item.item_status === "produced"
      );

      if (itemsProduced.length > 0) {
        const itemsSemKg = itemsProduced.filter(
          (item) => !item.quantity_produced_kg || item.quantity_produced_kg <= 0
        );

        if (itemsSemKg.length > 0) {
          const productsNames = itemsSemKg
            .map((item) => item.products?.name || "Produto desconhecido")
            .join(", ");
          toast.error(
            `⚠️ Os seguintes produtos marcados como "Produzido" não têm quantidade em KG preenchida: ${productsNames}`
          );
          return;
        }
      }

      // production_date nunca pode ser enviada como string vazia: o backend faz
      // cast direto para DATE e uma string vazia derruba a chamada com erro 500
      // em QUALQUER mudança de status (não só ao concluir). Por isso o fallback
      // para a data de hoje vale para todas as transições, não só "completed".
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      let completionDateToSend = completionDate;
      let productionDateToSend = productionDate || today;

      if (!productionDate) {
        setProductionDate(today);
      }

      if (newEstado === "completed" && !completionDate) {
        completionDateToSend = today;
        setCompletionDate(today);
      }

      // Atualizar status da ordem
      // Se o status for "completed" E houver items produzidos, enviar items para criar lotes
      const shouldCreateLots = newEstado === "completed" && itemsProduced.length > 0;

      const updatePayload = {
        order_number: productionOrder.order_number,
        order_name: productionOrder.order_name,
        start_date: productionOrder.start_date,
        status: newEstado,
        expected_completion_date: completionDateToSend,
        estimated_completion_date: estimatedDate,
        production_date: productionDateToSend,
        responsible_user_id: productionOrder.responsible_user_id,
      };

      // Sempre incluir items quando alterando status para garantir que os valores são persistidos
      // IMPORTANTE: Incluir quantity_produced_kg e parameters_filled para não perder esses dados
      updatePayload.items = productionOrder.production_order_items.map(item => ({
        product_id: item.product_id,
        suggested_quantity: item.suggested_quantity,
        quantity_to_produce: item.quantity_to_produce,
        actual_produced_quantity: item.actual_produced_quantity,
        quantity_produced_kg: item.quantity_produced_kg || 0,
        item_status: item.item_status,
        quality_grade: item.quality_grade,
        // Usar custom_lot_number salvo no banco ou do estado local
        custom_lot_number: item.custom_lot_number || localLotNumbers[item.id] || null,
        // Data real de produção (disponível para qualquer produto)
        actual_production_date: actualProductionDates[item.id] || null,
        // Preservar campos de parâmetros
        parameters_filled: item.parameters_filled || false,
        parameters_response_id: item.parameters_response_id || null,
      }));

      await productionService.updateProductionOrder(id, updatePayload);

      toast.success("Estado da ordem de produção atualizado com sucesso!");

      // Atualizar o estado local
      setProductionOrder((prev) => ({
        ...prev,
        status: newEstado,
        expected_completion_date: completionDateToSend,
        estimated_completion_date: estimatedDate,
        production_date: productionDateToSend
      }));

      // Se criou lotes, recarregar a página para mostrar os lotes criados
      if (shouldCreateLots) {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (err) {
      setError(err.message || "Erro ao atualizar status da ordem de produção");
      toast.error(
        err.message || "Erro ao atualizar status da ordem de produção"
      );
    }
  };

  const formatDate = (dateString) => {
    return dateString ? formatDateForDisplay(dateString) : "N/A";
  };
  
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    // Para campos de input, manter o formato original da data (YYYY-MM-DD)
    return dateString.split('T')[0];
  };
  
  const handleCompletionDateSave = async () => {
    try {
      // Não enviar items quando apenas alterando a data - isso evita que o RPC delete/recrie os itens
      await productionService.updateProductionOrder(id, {
        order_number: productionOrder.order_number,
        order_name: productionOrder.order_name,
        start_date: productionOrder.start_date,
        status: productionOrder.status,
        expected_completion_date: completionDate,
        estimated_completion_date: productionOrder.estimated_completion_date,
        production_date: productionOrder.production_date,
        responsible_user_id: productionOrder.responsible_user_id,
        // items NÃO enviado - apenas atualizar a data da ordem
      });
      setProductionOrder(prev => ({ ...prev, expected_completion_date: completionDate }));
      setEditingCompletionDate(false);
      toast.success("Data de conclusão atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar data de conclusão: " + err.message);
    }
  };

  const handleEstimatedDateSave = async () => {
    try {
      // Não enviar items quando apenas alterando a data - isso evita que o RPC delete/recrie os itens
      await productionService.updateProductionOrder(id, {
        order_number: productionOrder.order_number,
        order_name: productionOrder.order_name,
        start_date: productionOrder.start_date,
        status: productionOrder.status,
        expected_completion_date: productionOrder.expected_completion_date,
        estimated_completion_date: estimatedDate,
        production_date: productionOrder.production_date,
        responsible_user_id: productionOrder.responsible_user_id,
        // items NÃO enviado - apenas atualizar a data da ordem
      });
      setProductionOrder(prev => ({ ...prev, estimated_completion_date: estimatedDate }));
      setEditingEstimatedDate(false);
      toast.success("Data de previsão atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar data de previsão: " + err.message);
    }
  };

  const handleOrderNameSave = async () => {
    try {
      // Não enviar items quando apenas alterando o nome - isso evita que o RPC delete/recrie os itens
      await productionService.updateProductionOrder(id, {
        order_number: productionOrder.order_number,
        order_name: orderName,
        start_date: productionOrder.start_date,
        status: productionOrder.status,
        expected_completion_date: productionOrder.expected_completion_date,
        estimated_completion_date: productionOrder.estimated_completion_date,
        production_date: productionOrder.production_date,
        responsible_user_id: productionOrder.responsible_user_id,
        // items NÃO enviado - apenas atualizar o nome da ordem
      });
      setProductionOrder(prev => ({ ...prev, order_name: orderName }));
      setEditingOrderName(false);
      toast.success("Nome da ordem atualizado com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar nome da ordem: " + err.message);
    }
  };

  const handleProductionDateSave = async () => {
    try {
      // Não enviar items quando apenas alterando a data - isso evita que o RPC delete/recrie os itens
      await productionService.updateProductionOrder(id, {
        order_number: productionOrder.order_number,
        order_name: productionOrder.order_name,
        start_date: productionOrder.start_date,
        status: productionOrder.status,
        expected_completion_date: productionOrder.expected_completion_date,
        estimated_completion_date: productionOrder.estimated_completion_date,
        production_date: productionDate,
        responsible_user_id: productionOrder.responsible_user_id,
        // items NÃO enviado - apenas atualizar a data da ordem
      });
      setProductionOrder(prev => ({ ...prev, production_date: productionDate }));
      setEditingProductionDate(false);
      toast.success("Data de produção atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar data de produção: " + err.message);
    }
  };

  const productionEstadoColors = {
    pending: "bg-yellow-100 text-yellow-800",
    in_progress: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
  };

  const productionEstadoLabels = {
    pending: "Pendente",
    in_progress: "Em Andamento",
    completed: "Concluído",
    cancelled: "Cancelado",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <p>A carregar detalhes da ordem de produção...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center text-neutral-500">
          <p className="text-lg mb-2">Ordem de produção não encontrada</p>
          <p className="text-sm mb-6">A ordem solicitada não existe ou foi removida</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 p-3 sm:p-6 lg:p-8">
      <div className="max-w-[1320px] mx-auto">
        {/* Header compacto para mobile */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col space-y-3 sm:flex-row sm:justify-between sm:items-start sm:space-y-0">
            <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-neutral-800 leading-tight">
              Detalhes da Ordem
            </h1>
            <div className="flex items-center justify-between sm:flex-col sm:items-end space-x-3 sm:space-x-0 sm:space-y-2">
              <span
                className={`px-2 py-1 rounded-full text-xs sm:text-sm ${
                  productionEstadoColors[productionOrder.status]
                }`}
              >
                {productionEstadoLabels[productionOrder.status]}
              </span>
              <div className="relative">
                <button
                  className="bg-neutral-900 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-neutral-800 text-xs sm:text-sm"
                  onClick={(e) =>
                    e.currentTarget.nextElementSibling.classList.toggle(
                      "hidden"
                    )
                  }
                >
                  Atualizar
                </button>
                <div className="hidden absolute right-0 z-20 bg-white rounded-md shadow-lg min-w-[140px] border">
                  {Object.keys(productionEstadoLabels)
                    .filter((status) => status !== productionOrder.status)
                    .map((status) => (
                      <button
                        key={status}
                        onClick={() => handleUpdateEstado(status)}
                        className="block w-full text-left px-3 py-2 hover:bg-neutral-100 text-xs sm:text-sm"
                      >
                        {productionEstadoLabels[status]}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cards de informação */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white shadow-sm rounded-lg p-3 sm:p-4">
            <h3 className="text-sm sm:text-lg font-semibold text-neutral-800 mb-2 sm:mb-3">
              Informações da Ordem
            </h3>
            <div className="space-y-1.5 text-xs sm:text-sm">
              <div>
                <span className="font-medium text-neutral-600">Nº:</span>{" "}
                <span className="text-neutral-800">
                  {productionOrder.order_number}
                </span>
              </div>
              <div>
                <span className="font-medium text-neutral-600">Nome:</span>{" "}
                {editingOrderName ? (
                  <div className="inline-flex items-center space-x-2">
                    <input
                      type="text"
                      value={orderName}
                      onChange={(e) => setOrderName(e.target.value)}
                      className="px-2 py-1 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Nome da ordem"
                    />
                    <button
                      onClick={handleOrderNameSave}
                      className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => {
                        setEditingOrderName(false);
                        setOrderName(productionOrder.order_name || "");
                      }}
                      className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <span
                    className="text-neutral-800 cursor-pointer hover:bg-blue-100 px-1 rounded"
                    onClick={() => setEditingOrderName(true)}
                    title="Clique para editar o nome da ordem"
                  >
                    {productionOrder.order_name || "N/A"} ✏️
                  </span>
                )}
              </div>
              <div>
                <span className="font-medium text-neutral-600">Data de Produção:</span>{" "}
                {editingProductionDate ? (
                  <div className="inline-flex items-center space-x-2">
                    <input
                      type="date"
                      value={formatDateForInput(productionDate)}
                      onChange={(e) => setProductionDate(e.target.value)}
                      className="px-2 py-1 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                    />
                    <button
                      onClick={handleProductionDateSave}
                      className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => {
                        setEditingProductionDate(false);
                        setProductionDate(productionOrder.production_date || "");
                      }}
                      className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <span
                    className="text-neutral-800 cursor-pointer hover:bg-purple-100 px-1 rounded"
                    onClick={() => setEditingProductionDate(true)}
                    title="Clique para editar a data de produção"
                  >
                    {formatDate(productionOrder.production_date)} ✏️
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white shadow-sm rounded-lg p-3 sm:p-4">
            <h3 className="text-sm sm:text-lg font-semibold text-neutral-800 mb-2 sm:mb-3">
              Responsável
            </h3>
            <div className="text-xs sm:text-sm">
              {productionOrder.responsibleName ? (
                <div>
                  <span className="font-medium text-neutral-600">Nome:</span>{" "}
                  <span className="text-neutral-800">
                    {productionOrder.responsibleName}
                  </span>
                </div>
              ) : (
                <span className="text-neutral-500">N/A</span>
              )}
            </div>
          </div>
        </div>

        {/* Itens - Layout responsivo */}
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-4">
          <div className="mb-3 sm:mb-4">
            <h3 className="text-sm sm:text-lg font-semibold text-neutral-800">
              Itens da Produção
            </h3>
          </div>

          {/* Layout mobile - Cards */}
          <div className="block sm:hidden space-y-3">
            {productionOrder.production_order_items?.map((item) => (
              <div
                key={item.id}
                className="border border-neutral-200 rounded-lg p-3"
              >
                {/* Campo de número de lote para mobile - no topo */}
                <div className="mb-3">
                  <label className="block text-xs text-neutral-600 mb-1">
                    Nº do Lote
                  </label>
<input
                    type="text"
                    value={getLotNumberValue(item)}
                    onChange={(e) => {
                      handleLotNumberInputChange(item.id, e.target.value);
                    }}
                    placeholder={`Ex: ${generateLotNumber(item.products?.product_code)}`}
                    title="Número do lote - Campo editável - Salvamento automático"
                    className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 bg-white text-neutral-800 cursor-text border-green-300 hover:border-green-400 focus:ring-green-400 focus:border-green-500"
                  />
                </div>

                <div className="mb-3">
                  <label className="block text-xs text-neutral-600 mb-1">
                    Data Real de Produção
                  </label>
                  <input
                    type="date"
                    value={actualProductionDates[item.id] || ''}
                    onChange={(e) => handleActualProductionDateChange(item, e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 border-orange-300 hover:border-orange-400 focus:ring-orange-400 focus:border-orange-500 bg-orange-50"
                    title="Data em que o produto foi efetivamente produzido. Para produtos com cura, é usada para calcular o tempo de cura."
                  />
                </div>

                <div className="font-medium text-sm text-neutral-800 mb-2">
                  {item.products?.name || "N/A"}
                </div>

                <div className="mb-3">
                  <div>
                    <span className="text-xs text-neutral-600">A Produzir:</span>
                    <div className="font-medium text-sm text-blue-600">
                      {formatKgLiters(item)}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-neutral-600 mb-1">
                        Qtd. Produzida (L) <span className="text-neutral-400">(opcional)</span>
                      </label>
                      <input
                        type="number"
                        value={item.actual_produced_quantity || 0}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "actual_produced_quantity",
                            Number(e.target.value)
                          )
                        }
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 border-green-300 hover:border-green-400 focus:ring-green-400 focus:border-green-500"
                        placeholder="Opcional - para registro"
                        title="Salvamento automático"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-neutral-600 mb-1">
                        Qtd. Produzida (KG) <span className="text-red-500 font-bold">*</span>
                      </label>
                      <input
                        type="number"
                        value={item.quantity_produced_kg || 0}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "quantity_produced_kg",
                            Number(e.target.value)
                          )
                        }
                        min="0"
                        step="0.01"
                        className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 ${
                          productionOrder?.status === "completed"
                            ? "bg-neutral-100 cursor-not-allowed border-neutral-300"
                            : item.quantity_produced_kg > 0
                            ? "border-green-300 hover:border-green-400 focus:ring-green-400 focus:border-green-500"
                            : "border-red-300 focus:ring-red-500 focus:border-red-500"
                        }`}
                        placeholder="Obrigatório"
                        title="Salvamento automático"
                        required
                        disabled={productionOrder?.status === "completed"}
                      />
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="block text-xs text-neutral-600 mb-1">
                      Parâmetros de Produção
                    </label>
                    <ParameterStatusButton
                      hasActiveParameters={itemParametersStatus[item.id]?.hasActiveParameters}
                      parametersFilled={itemParametersStatus[item.id]?.parametersFilled}
                      onClick={() => handleOpenParameterModal(item)}
                      size="normal"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-neutral-600 mb-1">
                        Estado
                      </label>
                      <select
                        value={item.item_status || "pending"}
                        onChange={(e) => {
                          handleItemChange(
                            item.id,
                            "item_status",
                            e.target.value
                          );
                        }}
                        className="w-full px-2 py-1.5 border border-neutral-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-neutral-400"
                      >
                        {item.item_status !== "produced" && (
                          <option value="pending">Pendente</option>
                        )}
                        <option value="produced">Produzido</option>
                        {/* Só mostrar "Rejeitado" se o item já estiver produzido */}
                        {item.item_status === "produced" && (
                          <option value="rejected">Rejeitado</option>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs text-neutral-600 mb-1">
                        Qualidade
                      </label>
                      <select
                        value={item.quality_grade || "A"}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "quality_grade",
                            e.target.value
                          )
                        }
                        disabled={item.item_status !== "produced"}
                        className="w-full px-2 py-1.5 border border-neutral-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:bg-neutral-100"
                      >
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                      </select>
                    </div>
                  </div>

                  {productionOrder?.status !== "completed" && (
                    <button
                      onClick={() => handleOpenSplitModal(item)}
                      className="mt-2 w-full px-3 py-1.5 text-xs border border-amber-400 text-amber-700 bg-amber-50 rounded hover:bg-amber-100"
                    >
                      Dividir
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Layout desktop - Tabela */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead className="bg-neutral-50 border-b">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Nº do Lote
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Produto
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    A Produzir (kg/L)
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Produzida (L)
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Produzida (KG)
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Data Real Prod.
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Parâmetros
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Estado
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Qualidade
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {productionOrder.production_order_items?.map((item) => (
                  <tr key={item.id}>
                    <td className="px-3 py-3 text-sm">
<input
                        type="text"
                        value={getLotNumberValue(item)}
                        onChange={(e) => {
                          handleLotNumberInputChange(item.id, e.target.value);
                        }}
                        placeholder={`Ex: ${generateLotNumber(item.products?.product_code)}`}
                        title="Número do lote - Campo editável - Salvamento automático"
                        className="w-32 px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 bg-white text-neutral-800 cursor-text border-green-300 hover:border-green-400 focus:ring-green-400 focus:border-green-500"
                      />
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <div
                        className="max-w-[150px] truncate"
                        title={item.products?.name || "N/A"}
                      >
                        {item.products?.name || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <span className="text-blue-600 font-medium">
                        {formatKgLiters(item)}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        value={item.actual_produced_quantity || 0}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "actual_produced_quantity",
                            Number(e.target.value)
                          )
                        }
                        min="0"
                        step="0.01"
                        className="w-20 px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 border-green-300 hover:border-green-400 focus:ring-green-400 focus:border-green-500"
                        placeholder="Opcional"
                        title="Salvamento automático"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        value={item.quantity_produced_kg || 0}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "quantity_produced_kg",
                            Number(e.target.value)
                          )
                        }
                        min="0"
                        step="0.01"
                        className={`w-20 px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 ${
                          productionOrder?.status === "completed"
                            ? "bg-neutral-100 cursor-not-allowed border-neutral-300"
                            : item.quantity_produced_kg > 0
                            ? "border-green-300 hover:border-green-400 focus:ring-green-400 focus:border-green-500"
                            : "border-red-300 focus:ring-red-500 focus:border-red-500"
                        }`}
                        placeholder="Obrigatório"
                        title="Salvamento automático"
                        required
                        disabled={productionOrder?.status === "completed"}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="date"
                        value={actualProductionDates[item.id] || ''}
                        onChange={(e) => handleActualProductionDateChange(item, e.target.value)}
                        className="w-32 px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 border-orange-300 hover:border-orange-400 focus:ring-orange-400 focus:border-orange-500 bg-orange-50"
                        title="Data real de produção. Para produtos com cura, é usada para calcular o tempo de cura."
                      />
                    </td>
                    <td className="px-3 py-3">
                      <ParameterStatusButton
                        hasActiveParameters={itemParametersStatus[item.id]?.hasActiveParameters}
                        parametersFilled={itemParametersStatus[item.id]?.parametersFilled}
                        onClick={() => handleOpenParameterModal(item)}
                        size="normal"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <select
                        value={item.item_status || "pending"}
                        onChange={(e) => {
                          handleItemChange(
                            item.id,
                            "item_status",
                            e.target.value
                          );
                        }}
                        className="px-2 py-1.5 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400"
                      >
                        {item.item_status !== "produced" && (
                          <option value="pending">Pendente</option>
                        )}
                        <option value="produced">Produzido</option>
                        {/* Só mostrar "Rejeitado" se o item já estiver produzido */}
                        {item.item_status === "produced" && (
                          <option value="rejected">Rejeitado</option>
                        )}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      <select
                        value={item.quality_grade || "A"}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "quality_grade",
                            e.target.value
                          )
                        }
                        disabled={item.item_status !== "produced"}
                        className="w-16 px-2 py-1.5 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:bg-neutral-100"
                      >
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      {productionOrder?.status !== "completed" && (
                        <button
                          onClick={() => handleOpenSplitModal(item)}
                          className="px-3 py-1.5 text-sm border border-amber-400 text-amber-700 bg-amber-50 rounded hover:bg-amber-100 whitespace-nowrap"
                        >
                          Dividir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Botões de ação */}
        <div className="mt-4 sm:mt-6 flex justify-between sm:justify-end gap-2 sm:gap-3">
          {isAdmin && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-auto bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm"
              title="Deletar Ordem de Produção (Admin)"
            >
              🗑️ Deletar
            </button>
          )}
          <button
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto bg-neutral-100 text-neutral-900 px-4 py-2 rounded-md hover:bg-neutral-200 text-sm"
          >
            Voltar para Produção
          </button>
        </div>
      </div>

      {/* Modal de confirmação para rejeição de lote */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">
              Confirmar Rejeição do Lote
            </h3>
            <p className="text-neutral-600 mb-6">
              Tem certeza que deseja rejeitar este lote? Esta ação considerará o lote como baixa
              e a quantidade inicialmente produzida será removida do estoque.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelReject}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 rounded"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmReject}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded"
              >
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para deleção (ADMIN) */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-red-600 mb-4">
              ⚠️ Confirmar Deleção
            </h3>
            <p className="text-neutral-600 mb-6">
              Tem certeza que deseja deletar esta ordem de produção?
              <br />
              <strong className="text-red-600">Esta ação é irreversível!</strong>
              <br /><br />
              Ordem: {productionOrder?.order_name || productionOrder?.order_number || 'N/A'}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 rounded"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteProduction}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded"
              >
                Confirmar Deleção
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Divisão */}
      {showSplitModal && itemToSplit && (() => {
        const currentKg = itemToSplit.quantity_to_produce || 0;
        const produtividade = itemToSplit.products?.produtividade || 20;
        const splitKg = parseFloat(splitKilos) || 0;
        const splitL = splitKg > 0 ? (splitKg / produtividade) * 100 : 0;
        const remainKg = currentKg - splitKg;
        const remainL = remainKg > 0 ? (remainKg / produtividade) * 100 : 0;
        const isValid = splitKg > 0 && splitKg <= currentKg;

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-neutral-800 mb-1">
                Dividir Produção
              </h3>
              <p className="text-sm text-neutral-500 mb-4">
                {itemToSplit.products?.name || "Produto"}
              </p>

              <div className="bg-neutral-50 rounded-lg p-3 mb-4 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Quantidade atual:</span>
                  <span className="font-medium">
                    {currentKg.toFixed(2)} kg / {((currentKg / produtividade) * 100).toFixed(0)} L
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Kilos para nova ordem
                </label>
                <input
                  type="number"
                  value={splitKilos}
                  onChange={(e) => setSplitKilos(e.target.value)}
                  min="0.01"
                  max={currentKg}
                  step="0.01"
                  placeholder={`Máx. ${currentKg.toFixed(2)} kg`}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  autoFocus
                />
              </div>

              {splitKg > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Esta ordem ficará com:</span>
                    {remainKg === 0 ? (
                      <span className="font-medium text-orange-600 italic">Item removido desta ordem</span>
                    ) : (
                      <span className="font-medium text-neutral-800">
                        {remainKg.toFixed(2)} kg / {remainL.toFixed(0)} L
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between border-t border-amber-200 pt-1.5">
                    <span className="text-neutral-600">Nova ordem terá:</span>
                    <span className="font-medium text-amber-700">
                      {splitKg.toFixed(2)} kg / {splitL.toFixed(0)} L
                    </span>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowSplitModal(false); setItemToSplit(null); setSplitKilos(""); }}
                  className="px-4 py-2 text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 rounded text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmSplit}
                  disabled={!isValid}
                  className="px-4 py-2 bg-amber-600 text-white hover:bg-amber-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirmar Divisão
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Parâmetros de Produção */}
      <ParameterFormModal
        isOpen={parameterModalOpen}
        onClose={handleCloseParameterModal}
        productId={selectedItemForParameters?.product_id}
        productName={selectedItemForParameters?.products?.name}
        productionOrderItemId={selectedItemForParameters?.id}
        productionLotId={selectedLotForParameters?.id}
        existingResponses={existingResponses}
        onSave={handleParametersSaved}
      />

    </div>
  );
};

export default ProductionDetails;
